/** Pure Rank-screen data: tier ladder, next-tier unlocks, per-dungeon bests, rating history. */
import type { DungeonId } from '@keystone/sim';
import {
  type Profile,
  type RankStanding,
  type RankTierDef,
  type RatingRecord,
  type Unlock,
  RANK_TIERS,
  RATED_DUNGEONS,
  dungeonRating,
  profileStanding,
  tierDef,
  totalRating,
  updateRecord,
} from '@keystone/progression';

export const UNLOCK_TEXT: Readonly<Record<Unlock, string>> = {
  gems: 'Gems: socket, fuse and earn Gem Power bonuses',
  reforge: 'Reforging: swap one secondary stat per item',
  legendaries: 'Legendary drops',
  capstones: 'Talent capstones (tier 3)',
  highGemRanks: 'Higher gem ranks',
  maxUpgrade: 'Maximum drop and upgrade item level',
};

export interface TierPerk {
  kind: 'ilvl' | 'gemRank' | 'legendary' | 'talents' | 'unlock';
  text: string;
  /** Sub-rank it arrives at, when not the first. */
  atSubRank?: number;
}

/** What entering `t` changes compared with `prev` (the tier below). */
export function tierPerks(t: RankTierDef, prev: RankTierDef | null): TierPerk[] {
  const out: TierPerk[] = [];
  if (!prev || t.maxIlvl > prev.maxIlvl) out.push({ kind: 'ilvl', text: `Max item level ${t.maxIlvl}${prev ? ` (from ${prev.maxIlvl})` : ''}` });
  if (t.maxGemRank > (prev?.maxGemRank ?? 0)) out.push({ kind: 'gemRank', text: `Gems up to rank ${t.maxGemRank}` });
  if (t.legendaryChance > (prev?.legendaryChance ?? 0)) out.push({ kind: 'legendary', text: `Legendary drop chance ${Math.round(t.legendaryChance * 100)}%` });
  if (t.talentTiers > (prev?.talentTiers ?? 0)) out.push({ kind: 'talents', text: t.talentTiers >= 3 ? 'Talent tier 3 and capstones' : `Talent tiers 1–${t.talentTiers}` });
  for (const u of t.unlocks) {
    if (u.unlock === 'capstones' && out.some((x) => x.kind === 'talents')) continue;
    if (u.unlock === 'legendaries' && out.some((x) => x.kind === 'legendary')) continue;
    if (u.unlock === 'highGemRanks' && out.some((x) => x.kind === 'gemRank')) continue;
    if (u.unlock === 'maxUpgrade' && out.some((x) => x.kind === 'ilvl')) continue;
    out.push({ kind: 'unlock', text: UNLOCK_TEXT[u.unlock], ...(u.atSubRank && u.atSubRank > 1 ? { atSubRank: u.atSubRank } : {}) });
  }
  return out;
}

export interface TierRow {
  def: RankTierDef;
  status: 'done' | 'current' | 'locked';
  perks: TierPerk[];
}

export function tierLadder(s: RankStanding): TierRow[] {
  const cur = RANK_TIERS.findIndex((t) => t.tier === s.tier);
  return RANK_TIERS.map((def, i) => ({ def, status: i < cur ? 'done' : i === cur ? 'current' : 'locked', perks: tierPerks(def, RANK_TIERS[i - 1] ?? null) }));
}

/** The next tier and what it brings, plus perks still waiting inside the current tier. */
export function nextUnlocks(s: RankStanding): { next: RankTierDef | null; perks: TierPerk[]; withinTier: TierPerk[] } {
  const idx = RANK_TIERS.findIndex((t) => t.tier === s.tier);
  const cur = tierDef(s.tier);
  const next = RANK_TIERS[idx + 1] ?? null;
  const withinTier = cur.unlocks.filter((u) => (u.atSubRank ?? 1) > s.subRank).map((u) => ({ kind: 'unlock' as const, text: UNLOCK_TEXT[u.unlock], atSubRank: u.atSubRank! }));
  return { next, perks: next ? tierPerks(next, cur) : [], withinTier };
}

// ---------------------------------------------------------------------------
// Dungeons
// ---------------------------------------------------------------------------

export interface DungeonRow {
  id: DungeonId;
  name: string;
  bestTimed: number | null;
  score: number;
  /** Contribution to rating after scaling. */
  rating: number;
  bestAttempt: { level: number; timed: boolean } | null;
  /** Fraction of the timer left on the best timed run. */
  timeLeft: number | null;
}

export function dungeonRows(p: Profile, dungeons: readonly { id: DungeonId; name: string }[], rated: readonly DungeonId[] = RATED_DUNGEONS): DungeonRow[] {
  return dungeons
    .filter((d) => rated.includes(d.id))
    .map((d) => {
      const rec: RatingRecord | undefined = p.rating[d.id];
      const best = rec?.best ?? null;
      return {
        id: d.id,
        name: d.name,
        bestTimed: best?.level ?? null,
        score: best?.score ?? 0,
        rating: dungeonRating(rec, rated),
        bestAttempt: rec?.bestAttempt ? { level: rec.bestAttempt.level, timed: rec.bestAttempt.timed } : null,
        timeLeft: best && best.timerMs > 0 ? Math.max(0, (best.timerMs - best.elapsedMs) / best.timerMs) : null,
      };
    });
}

// ---------------------------------------------------------------------------
// Rating history
// ---------------------------------------------------------------------------

/**
 * Rating after each run, rebuilt by replaying the run log through `updateRecord` (runs are never
 * truncated, so this is exact). Starts with the rating before the first run (0).
 */
export function ratingHistory(p: Profile, rated: readonly DungeonId[] = RATED_DUNGEONS): number[] {
  const recs: Record<DungeonId, RatingRecord> = {};
  const out = [0];
  for (const r of [...p.runs].sort((a, b) => a.runIndex - b.runIndex)) {
    recs[r.dungeonId] = updateRecord(recs[r.dungeonId], r);
    out.push(totalRating(recs, rated));
  }
  return out;
}

export interface Sparkline {
  /** SVG polyline `points`. */
  points: string;
  /** Closed path for the area fill. */
  area: string;
  min: number;
  max: number;
  last: { x: number; y: number } | null;
}

/** Points for a w×h sparkline (y grows downward); a flat or single-value series sits at the bottom. */
export function sparkline(values: readonly number[], w: number, h: number, pad = 2): Sparkline {
  if (!values.length) return { points: '', area: '', min: 0, max: 0, last: null };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const n = values.length;
  const xy = values.map((v, i) => {
    const x = n === 1 ? w / 2 : pad + (i * (w - 2 * pad)) / (n - 1);
    const y = span === 0 ? h - pad : h - pad - ((v - min) / span) * (h - 2 * pad);
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
  });
  const points = xy.map((p) => `${p.x},${p.y}`).join(' ');
  const first = xy[0]!;
  const last = xy[n - 1]!;
  const area = `M${first.x},${h} L${xy.map((p) => `${p.x},${p.y}`).join(' L')} L${last.x},${h} Z`;
  return { points, area, min, max, last };
}

export function profileRankView(p: Profile): { standing: RankStanding; history: number[] } {
  return { standing: profileStanding(p), history: ratingHistory(p) };
}
